import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';

const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const navHeight = document.querySelector('header')?.offsetHeight || 0;
      const bannerHeight = 36; // Height of LaunchBanner
      const sectionTop = section.getBoundingClientRect().top + window.pageYOffset - navHeight - bannerHeight;
      
      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth'
      });
      
      // Close mobile menu after navigation
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header 
      className={`fixed left-0 w-full z-50 transition-all duration-300 bg-header-scrolled backdrop-blur-sm ${scrolled ? 'shadow-lg' : ''}`}
      style={{ top: '36px' }}
    >
      <div className="container mx-auto px-6 flex justify-between items-center h-20">
        {/* Left-aligned Logo */}
        <button onClick={scrollToTop} className="focus:outline-none">
          <img 
            src={logo} 
            alt="BornFree logo" 
            className="h-12 transition-transform duration-300 hover:scale-105"
            style={{ filter: 'drop-shadow(0 0 18px rgba(54, 169, 247, 0.9)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))' }}
          />
        </button>

        {/* Centered navigation links - Desktop */}
        <nav className="hidden md:flex items-center space-x-8">
          <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-lg font-semibold uppercase tracking-wider transition-all duration-300" style={{ color: '#000000', textShadow: '0 0 18px rgba(54, 169, 247, 0.9), 0 0 8px rgba(255, 255, 255, 0.8), 0 0 24px rgba(54, 169, 247, 0.7)' }} onMouseEnter={(e) => (e.target as HTMLElement).style.color = '#FFFFFF'} onMouseLeave={(e) => (e.target as HTMLElement).style.color = '#000000'}>About</a>
          <a href="#tech" onClick={(e) => { e.preventDefault(); scrollToSection('tech'); }} className="text-lg font-semibold uppercase tracking-wider transition-all duration-300" style={{ color: '#000000', textShadow: '0 0 18px rgba(54, 169, 247, 0.9), 0 0 8px rgba(255, 255, 255, 0.8), 0 0 24px rgba(54, 169, 247, 0.7)' }} onMouseEnter={(e) => (e.target as HTMLElement).style.color = '#FFFFFF'} onMouseLeave={(e) => (e.target as HTMLElement).style.color = '#000000'}>Tech</a>
          <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }} className="text-lg font-semibold uppercase tracking-wider transition-all duration-300" style={{ color: '#000000', textShadow: '0 0 18px rgba(54, 169, 247, 0.9), 0 0 8px rgba(255, 255, 255, 0.8), 0 0 24px rgba(54, 169, 247, 0.7)' }} onMouseEnter={(e) => (e.target as HTMLElement).style.color = '#FFFFFF'} onMouseLeave={(e) => (e.target as HTMLElement).style.color = '#000000'}>FAQ</a>
          <a href="#community" onClick={(e) => { e.preventDefault(); scrollToSection('community'); }} className="text-lg font-semibold uppercase tracking-wider transition-all duration-300" style={{ color: '#000000', textShadow: '0 0 18px rgba(54, 169, 247, 0.9), 0 0 8px rgba(255, 255, 255, 0.8), 0 0 24px rgba(54, 169, 247, 0.7)' }} onMouseEnter={(e) => (e.target as HTMLElement).style.color = '#FFFFFF'} onMouseLeave={(e) => (e.target as HTMLElement).style.color = '#000000'}>Community</a>
        </nav>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Right-aligned action button */}
        <div className="hidden md:block">
          <a 
            href="https://app.bornfree.io/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="bg-black text-white font-semibold py-3 px-3 sm:px-6 rounded-full transition-all duration-300 hover:bg-gray-800 whitespace-nowrap text-sm sm:text-base"
            style={{ boxShadow: '0 0 18px rgba(54, 169, 247, 0.9), 0 0 8px rgba(255, 255, 255, 0.8)' }}
          >
            Launch App
          </a>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed left-0 right-0 bg-gray-900 bg-opacity-95 backdrop-blur-sm shadow-lg"
          style={{ top: 'calc(36px + 80px)', zIndex: 40 }}
        >
          <nav className="flex flex-col p-4 space-y-4">
            <a 
              href="#about" 
              onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} 
              className="text-white text-lg font-semibold uppercase tracking-wider py-2 hover:text-cyan-400 transition-colors"
            >
              About
            </a>
            <a 
              href="#tech" 
              onClick={(e) => { e.preventDefault(); scrollToSection('tech'); }} 
              className="text-white text-lg font-semibold uppercase tracking-wider py-2 hover:text-cyan-400 transition-colors"
            >
              Tech
            </a>
            <a 
              href="#faq" 
              onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }} 
              className="text-white text-lg font-semibold uppercase tracking-wider py-2 hover:text-cyan-400 transition-colors"
            >
              FAQ
            </a>
            <a 
              href="#community" 
              onClick={(e) => { e.preventDefault(); scrollToSection('community'); }} 
              className="text-white text-lg font-semibold uppercase tracking-wider py-2 hover:text-cyan-400 transition-colors"
            >
              Community
            </a>
            <a 
              href="https://app.bornfree.io/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3 px-6 rounded-lg text-center transition-colors"
            >
              Launch App
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navigation;